import type { GlobalState } from '../global/types';

import { IS_MOCKED_CLIENT } from '../config';
import { loadCache, loadCachedSharedState, migrateCache } from '../global/cache';
import {
  getGlobal, setGlobal,
} from '../global/index';
import { INITIAL_GLOBAL_STATE } from '../global/initialState';
import { clearPasscodeSettings, updatePasscodeSettings } from '../global/reducers';
import { cloneDeep } from './iteratees';
import { ACCOUNT_SLOT } from './multiaccount';
import {
  decryptSessionByCurrentHash, markPasscodeStored, readStoredPasscodeState, restorePasscodeForTab,
} from './passcode';
import { clearStoredSessionKeepAccountInfo, markScreenUnlocked, storeSession } from './sessions';

export async function initGlobal(force: boolean = false, prevGlobal?: GlobalState) {
  prevGlobal = prevGlobal || getGlobal();
  if (!force && 'byTabId' in prevGlobal) {
    return;
  }

  const initial = cloneDeep(INITIAL_GLOBAL_STATE);
  let cache: GlobalState | undefined;
  try {
    cache = await loadCache(initial);
  } catch (err) {
    // A storage that cannot be opened (corrupt IndexedDB profile, private mode, disk pressure) used
    // to reject out of `init` before anything rendered — a blank page with no message. Boot without
    // the cache instead; the session is re-fetched from the server like a first sign-in.
    // eslint-disable-next-line no-console
    console.error('[Init] Failed to load the cached state, starting clean', err);
  }
  let global = cache || initial;
  if (IS_MOCKED_CLIENT) global.auth.state = 'authorizationStateReady';

  // The passcode belongs to the device, not to one account: an account that never set one still locks
  // and still reports it as enabled, and one that had it turned off elsewhere must stop locking even
  // though its own cached state still says otherwise.
  const storedState = readStoredPasscodeState();
  const hasPasscode = storedState ?? global.passcode.hasPasscode;
  if (hasPasscode !== global.passcode.hasPasscode) {
    global = hasPasscode
      ? updatePasscodeSettings(global, { hasPasscode: true })
      : clearPasscodeSettings(global);
  }

  // A passcode set before it became device-wide is only recorded in the account that set it
  if (storedState === undefined && hasPasscode) {
    markPasscodeStored();
  }

  if (hasPasscode && !global.passcode.isScreenLocked) {
    const restored = await restoreUnlockedState(global);
    if (restored) {
      global = restored;
    } else {
      global = updatePasscodeSettings(global, {
        isScreenLocked: true,
      });

      clearStoredSessionKeepAccountInfo(ACCOUNT_SLOT);
    }
  }

  if (force) {
    global.byTabId = prevGlobal.byTabId;
  }

  if (!cache) { // Try loading shared state separately
    const storedSharedState = await loadCachedSharedState();
    if (storedSharedState) {
      global.sharedState = storedSharedState;
    }
  }

  setGlobal(global);
}

// Switching accounts is a full page load, so staying unlocked across one means picking the key back
// up from the tab it was unlocked in. Returns the restored state, or `undefined` to lock as usual.
async function restoreUnlockedState<T extends GlobalState>(global: T): Promise<T | undefined> {
  if (!restorePasscodeForTab()) return undefined;

  try {
    const { sessionJson, globalJson } = await decryptSessionByCurrentHash();
    markScreenUnlocked();
    if (sessionJson) {
      storeSession(JSON.parse(sessionJson));
    }

    // Absent for an account that has never locked — its cached state was never encrypted away
    if (!globalJson) return global;

    const decrypted = JSON.parse(globalJson) as T;
    migrateCache(decrypted, cloneDeep(INITIAL_GLOBAL_STATE));
    return updatePasscodeSettings(decrypted, { hasPasscode: true, isScreenLocked: false });
  } catch (err) {
    // A key that no longer opens this account's blobs is no better than none: fall back to the lock
    return undefined;
  }
}
